// StudioMicProcessor — Udyog production audio pipeline (v2)
// -----------------------------------------------------------------------------
// LiveKit TrackProcessor. Three selectable modes:
//
//   'off'         Raw mic (browser AEC still active, everything else disabled)
//   'dfn3-only'   MediaStreamSource -> DFN3 -> Destination
//                 Pure AI denoise. Most natural voice, no broadcast processing.
//   'dfn3+dsp'    MediaStreamSource -> DFN3 -> HighPass 80 Hz -> Compressor
//                 -> +3 dB -> Limiter -> Destination
//                 DEFAULT. Broadcast character on top of AI denoise.
//
// The pre-DFN3 NoiseGate that lived in v1 has been REMOVED. It was clipping
// word beginnings and low-energy fricatives, and DFN3 already gates internally
// via its deep filter; running our own gate first was strictly harmful.
//
// Foundation (in Meeting.jsx getUserMedia constraints, outside this processor):
//   * echoCancellation: true    — Chrome AEC3, kept
//   * noiseSuppression: false   — DFN3 needs a raw signal
//   * autoGainControl:  false   — mic distance should control loudness (physics)
// -----------------------------------------------------------------------------
import { DeepFilterNet3Core } from "deepfilternet3-noise-filter";

const PUBLIC_URL = process.env.PUBLIC_URL || "";
const DFN3_ASSETS_BASE = `${PUBLIC_URL}/wasm/deepfilternet3`;

export const MODES = ["off", "dfn3-only", "dfn3+dsp"];
export const DEFAULT_MODE = "dfn3+dsp";

// Basic runtime capability probe. Silently returns 'off' on browsers without
// WebAssembly SIMD (very old Chromium / Safari < 16.4) so we never crash a
// meeting on an incompatible device.
export function detectAudioMode(preferredMode) {
  try {
    if (typeof WebAssembly === "undefined") { console.warn("[studio-mic] WebAssembly unavailable — falling back to raw mic"); return "off"; }
    if (typeof AudioWorkletNode === "undefined") { console.warn("[studio-mic] AudioWorklet unavailable — falling back to raw mic"); return "off"; }
    // WASM SIMD detection — 12-byte SIMD-only bytecode.
    const simdSupported = WebAssembly.validate(new Uint8Array([
      0, 97, 115, 109, 1, 0, 0, 0, 1, 5, 1, 96, 0, 1, 123, 3, 2, 1, 0,
      10, 10, 1, 8, 0, 65, 0, 253, 15, 253, 98, 11,
    ]));
    if (!simdSupported) { console.warn("[studio-mic] WASM SIMD unsupported — falling back to raw mic"); return "off"; }
  } catch (e) {
    console.warn("[studio-mic] capability probe threw:", e?.message || e);
    return "off";
  }
  return MODES.includes(preferredMode) ? preferredMode : DEFAULT_MODE;
}

// -----------------------------------------------------------------------------
// PRE-WARM
// DFN3's WASM + model + AudioWorklet take 3-7s to load on a mid-range phone.
// Doing that on the mic click meant the first seconds of speech never reached
// the class. So we build the expensive half of the pipeline (AudioContext +
// DFN3 core + worklet node) in the background as soon as the meeting page
// opens, and the processor just plugs the mic into it later — instantly.
// -----------------------------------------------------------------------------
let warmRig = null;
let warmPromise = null;

export function prewarmStudioMic(opts = {}) {
  if (warmPromise) return warmPromise;
  const mode = detectAudioMode(opts.mode ?? DEFAULT_MODE);
  if (mode === "off") return Promise.resolve(null);
  const level = Number.isFinite(opts.suppressionLevel) ? opts.suppressionLevel : 80;

  warmPromise = (async () => {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    const ctx = new AC({ sampleRate: 48000, latencyHint: "interactive" });
    if (ctx.sampleRate !== 48000) {
      try { await ctx.close(); } catch (_) {}
      return null;
    }
    const dfn3 = new DeepFilterNet3Core({
      sampleRate: 48000,
      noiseReductionLevel: level,
      assetConfig: { cdnUrl: DFN3_ASSETS_BASE },
    });
    await dfn3.initialize();
    const node = await dfn3.createAudioWorkletNode(ctx);
    warmRig = { ctx, dfn3, node };
    console.info("[studio-mic] pre-warmed — mic will be instant");
    return warmRig;
  })().catch((e) => {
    console.warn("[studio-mic] pre-warm failed:", e?.message || e);
    warmRig = null;
    return null;
  });

  return warmPromise;
}

function takeWarmRig() {
  const rig = warmRig;
  warmRig = null;
  warmPromise = null;
  return rig;
}

export async function destroyWarmStudioMic() {
  const rig = takeWarmRig();
  if (!rig) return;
  try { rig.node?.disconnect(); } catch (_) {}
  try { rig.dfn3?.destroy(); } catch (_) {}
  try { if (rig.ctx?.state !== "closed") await rig.ctx.close(); } catch (_) {}
}

export class StudioMicProcessor {
  constructor(opts = {}) {
    this.name = "udyog-studio-mic-dfn3";
    this.processedTrack = undefined;

    this._mode = detectAudioMode(opts.mode ?? DEFAULT_MODE);
    this._suppressionLevel = Number.isFinite(opts.suppressionLevel)
      ? opts.suppressionLevel
      : 80;

    this._ctx = null;
    this._dfn3 = null;
    this._source = null;
    this._srcTrack = null;
    this._dfn3Node = null;
    this._highpass = null;
    this._compressor = null;
    this._makeup = null;
    this._limiter = null;
    this._destination = null;
  }

  get mode() { return this._mode; }

  get audioContext() { return this._ctx; }

  /** False when the pipeline has gone deaf without throwing — the phone
   *  suspended the AudioContext in the background, or the mic track behind
   *  our MediaStreamSource was replaced/ended by the browser. */
  isHealthy() {
    if (this._mode === "off") return true;
    if (!this._ctx || this._ctx.state === "closed") return false;
    if (!this._srcTrack || this._srcTrack.readyState !== "live") return false;
    const out = this.processedTrack;
    if (!out || out.readyState !== "live") return false;
    return true;
  }

  async init(opts) {
    console.info(`[studio-mic] init(mode=${this._mode})`);

    // Mode 'off' is degenerate — nothing to do here, LiveKit will keep the
    // raw track. We still throw so Meeting.jsx knows to skip attachment.
    if (this._mode === "off") {
      throw new Error("[studio-mic] mode=off — no processor attached");
    }

    // Reuse the rig built during pre-warm when it's ready — this is what makes
    // the mic go live instantly instead of after the WASM load.
    const warm = takeWarmRig();
    if (warm) {
      this._ctx = warm.ctx;
      this._dfn3 = warm.dfn3;
      this._dfn3Node = warm.node;
      if (this._ctx.state === "suspended") {
        try { await this._ctx.resume(); } catch (_) {}
      }
      try { this._dfn3.setSuppressionLevel?.(this._suppressionLevel); } catch (_) {}
    } else {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) throw new Error("Web Audio API not available");

      this._ctx = new AC({ sampleRate: 48000, latencyHint: "interactive" });
      if (this._ctx.sampleRate !== 48000) {
        const actual = this._ctx.sampleRate;
        try { await this._ctx.close(); } catch (_) {}
        this._ctx = null;
        throw new Error(`AudioContext sample rate is ${actual} Hz, need 48000 Hz`);
      }
      if (this._ctx.state === "suspended") {
        try { await this._ctx.resume(); } catch (_) {}
      }

      // ---- Load DFN3 model + worklet ----
      this._dfn3 = new DeepFilterNet3Core({
        sampleRate: 48000,
        noiseReductionLevel: this._suppressionLevel,
        assetConfig: { cdnUrl: DFN3_ASSETS_BASE },
      });
      await this._dfn3.initialize();
      this._dfn3Node = await this._dfn3.createAudioWorkletNode(this._ctx);
    }

    this._source = this._ctx.createMediaStreamSource(new MediaStream([opts.track]));
    this._srcTrack = opts.track;
    this._destination = this._ctx.createMediaStreamDestination();

    if (this._mode === "dfn3-only") {
      // Purest chain — AI denoise only, no broadcast processing.
      this._source.connect(this._dfn3Node).connect(this._destination);
    } else {
      // 'dfn3+dsp' — broadcast chain, tuned gentler than v1:
      //   * HighPass 80 Hz Q 0.707 — kills rumble, keeps male vocal warmth
      //   * Compressor threshold -18 dB (was -22), knee 6 dB (was 12),
      //     ratio 3:1, release 250 ms — engages only when voice > -18 dB RMS
      //     so quiet speech stays untouched.
      //   * Makeup +3 dB (was +5) — safer across loud studio mics.
      //   * Limiter unchanged (brick wall @ -1 dBFS).
      this._highpass = this._ctx.createBiquadFilter();
      this._highpass.type = "highpass";
      this._highpass.frequency.value = 80;
      this._highpass.Q.value = 0.707;

      this._compressor = this._ctx.createDynamicsCompressor();
      this._compressor.threshold.value = -18;
      this._compressor.knee.value = 6;
      this._compressor.ratio.value = 3;
      this._compressor.attack.value = 0.005;
      this._compressor.release.value = 0.25;

      this._makeup = this._ctx.createGain();
      this._makeup.gain.value = 1.41; // ~+3 dB

      this._limiter = this._ctx.createDynamicsCompressor();
      this._limiter.threshold.value = -1;
      this._limiter.knee.value = 0;
      this._limiter.ratio.value = 20;
      this._limiter.attack.value = 0.001;
      this._limiter.release.value = 0.05;

      this._source
        .connect(this._dfn3Node)
        .connect(this._highpass)
        .connect(this._compressor)
        .connect(this._makeup)
        .connect(this._limiter)
        .connect(this._destination);
    }

    this.processedTrack = this._destination.stream.getAudioTracks()[0];
    console.info(`[studio-mic] active (mode=${this._mode})`);
  }

  async restart(opts) {
    await this.destroy();
    await this.init(opts);
  }

  setSuppressionLevel(level) {
    this._suppressionLevel = level;
    try { this._dfn3?.setSuppressionLevel(level); } catch (_) {}
  }

  setNoiseSuppressionEnabled(enabled) {
    try { this._dfn3?.setNoiseSuppressionEnabled(enabled); } catch (_) {}
  }

  async destroy() {
    try {
      this._source && this._source.disconnect();
      this._dfn3Node && this._dfn3Node.disconnect();
      this._highpass && this._highpass.disconnect();
      this._compressor && this._compressor.disconnect();
      this._makeup && this._makeup.disconnect();
      this._limiter && this._limiter.disconnect();
      this._destination && this._destination.disconnect();
      this._dfn3 && this._dfn3.destroy();
    } catch (_) {}
    try {
      if (this._ctx && this._ctx.state !== "closed") {
        await this._ctx.close();
      }
    } catch (_) {}
    this._ctx = null;
    this._dfn3 = null;
    this._source = null;
    this._srcTrack = null;
    this._dfn3Node = null;
    this._highpass = null;
    this._compressor = null;
    this._makeup = null;
    this._limiter = null;
    this._destination = null;
    this.processedTrack = undefined;
  }
}

// -----------------------------------------------------------------------------
// User preferences — Studio Mic ON, mode = dfn3+dsp by default. All persisted
// in localStorage. Toggle via long-press mic button; mode selectable on the
// /audio-test page.
// -----------------------------------------------------------------------------
const KEY_ON = "udyog:studioMic";
const KEY_MODE = "udyog:studioMicMode";

export function getStudioMicPref() {
  try {
    const v = localStorage.getItem(KEY_ON);
    return v === null ? true : v !== "0";
  } catch (_) { return true; }
}
export function setStudioMicPref(on) {
  try { localStorage.setItem(KEY_ON, on ? "1" : "0"); } catch (_) {}
}
export function getStudioMicMode() {
  try {
    const v = localStorage.getItem(KEY_MODE);
    return MODES.includes(v) ? v : DEFAULT_MODE;
  } catch (_) { return DEFAULT_MODE; }
}
export function setStudioMicMode(mode) {
  if (!MODES.includes(mode)) return;
  try { localStorage.setItem(KEY_MODE, mode); } catch (_) {}
}
